<script setup lang="ts">
import { computed } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import { getQualityAnalytics, type ParetoItem } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["quality-analytics", selectedProjectId],
  queryFn: () => getQualityAnalytics(selectedProjectId.value),
});

const ftrPct = computed(() => {
  const d = data.value;
  if (!d || d.approved === 0) return null;
  return Math.round((d.first_time_right / d.approved) * 100);
});

// Scale a list of counts to its own max for the bar widths.
function barPct(items: ParetoItem[], count: number): number {
  const max = Math.max(1, ...items.map((i) => i.count));
  return Math.round((count / max) * 100);
}

const trendMax = computed(() =>
  Math.max(1, ...(data.value?.trend ?? []).map((t) => t.signed_off)),
);
</script>

<template>
  <PageLayout wide>
    <h1 class="mb-1 text-xl font-semibold">
      Quality insights
    </h1>
    <p class="mb-4 text-sm text-neutral-500">
      Trends and recurring issues across this project's ITP check sheets — the
      evidence behind Pareto and root-cause.
    </p>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.total === 0"
      class="rounded-xl bg-neutral-0 p-8 text-center text-neutral-500 shadow"
    >
      No check sheets completed on this project yet — insights appear once
      inspections are signed off.
    </div>
    <div
      v-else-if="data"
      class="grid gap-6"
    >
      <!-- KPI cards -->
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            ITPs
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ data.total }}
          </p>
          <p class="text-xs text-neutral-500">
            {{ data.by_status.approved }} approved ·
            {{ data.by_status.signed_off }} awaiting CRE
          </p>
        </div>
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            First-time-right
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ ftrPct === null ? "—" : ftrPct + "%" }}
          </p>
          <p class="text-xs text-neutral-500">
            {{ data.first_time_right }} of {{ data.approved }} approved, never
            sent back
          </p>
        </div>
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Reopened
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ data.reopened_ever }}
          </p>
          <p class="text-xs text-neutral-500">
            sheets sent back at least once
          </p>
        </div>
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Defects
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ data.defect_total }}
          </p>
          <p class="text-xs text-neutral-500">
            raised across inspections
          </p>
        </div>
      </div>

      <div class="grid gap-6 lg:grid-cols-2">
        <!-- Recurring non-conformances (Pareto) -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow">
          <h2 class="mb-1 font-semibold">
            Recurring non-conformances
          </h2>
          <p class="mb-4 text-xs text-neutral-400">
            The items that fail most often across inspections — where to focus.
          </p>
          <p
            v-if="!data.top_nonconformances.length"
            class="text-sm text-neutral-400"
          >
            No non-conformances recorded. 👍
          </p>
          <ul
            v-else
            class="grid gap-2"
          >
            <li
              v-for="item in data.top_nonconformances"
              :key="item.label"
            >
              <div class="flex justify-between text-sm">
                <span class="truncate pr-2 text-neutral-700">{{ item.label }}</span>
                <span class="font-medium text-neutral-500">{{ item.count }}</span>
              </div>
              <div class="mt-1 h-2 rounded-full bg-neutral-100">
                <div
                  class="h-2 rounded-full bg-semantic-warning"
                  :style="{ width: barPct(data.top_nonconformances, item.count) + '%' }"
                />
              </div>
            </li>
          </ul>
        </section>

        <!-- Non-conformances by ITP type -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow">
          <h2 class="mb-1 font-semibold">
            Non-conformances by ITP type
          </h2>
          <p class="mb-4 text-xs text-neutral-400">
            Which check-sheet types generate the most issues.
          </p>
          <p
            v-if="!data.nonconformances_by_template.length"
            class="text-sm text-neutral-400"
          >
            Nothing recorded yet.
          </p>
          <ul
            v-else
            class="grid gap-2"
          >
            <li
              v-for="item in data.nonconformances_by_template"
              :key="item.label"
            >
              <div class="flex justify-between text-sm">
                <span class="truncate pr-2 text-neutral-700">{{ item.label }}</span>
                <span class="font-medium text-neutral-500">{{ item.count }}</span>
              </div>
              <div class="mt-1 h-2 rounded-full bg-neutral-100">
                <div
                  class="h-2 rounded-full bg-brand-primary"
                  :style="{ width: barPct(data.nonconformances_by_template, item.count) + '%' }"
                />
              </div>
            </li>
          </ul>
        </section>

        <!-- Defects by category -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow">
          <h2 class="mb-4 font-semibold">
            Defects by category
          </h2>
          <p
            v-if="!data.defects_by_category.length"
            class="text-sm text-neutral-400"
          >
            No defects raised.
          </p>
          <ul
            v-else
            class="grid gap-2"
          >
            <li
              v-for="item in data.defects_by_category"
              :key="item.label"
            >
              <div class="flex justify-between text-sm">
                <span class="capitalize text-neutral-700">{{ item.label }}</span>
                <span class="font-medium text-neutral-500">{{ item.count }}</span>
              </div>
              <div class="mt-1 h-2 rounded-full bg-neutral-100">
                <div
                  class="h-2 rounded-full bg-semantic-danger"
                  :style="{ width: barPct(data.defects_by_category, item.count) + '%' }"
                />
              </div>
            </li>
          </ul>
        </section>

        <!-- Weekly trend -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow">
          <h2 class="mb-1 font-semibold">
            Trend by week
          </h2>
          <p class="mb-4 text-xs text-neutral-400">
            Sign-offs (bar) and non-conformances (amber) per week.
          </p>
          <p
            v-if="!data.trend.length"
            class="text-sm text-neutral-400"
          >
            No signed-off sheets yet.
          </p>
          <div
            v-else
            class="flex items-end gap-2"
            style="height: 140px"
          >
            <div
              v-for="t in data.trend"
              :key="t.week"
              class="flex flex-1 flex-col items-center justify-end gap-1"
              :title="`${t.week}: ${t.signed_off} signed off, ${t.nonconformances} NC`"
            >
              <div class="flex w-full items-end justify-center gap-0.5">
                <div
                  class="w-2.5 rounded-t bg-brand-primary"
                  :style="{ height: (t.signed_off / trendMax) * 110 + 'px' }"
                />
                <div
                  class="w-2.5 rounded-t bg-semantic-warning"
                  :style="{ height: (t.nonconformances / trendMax) * 110 + 'px' }"
                />
              </div>
              <span class="text-[10px] text-neutral-400">{{ t.week.slice(5) }}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  </PageLayout>
</template>
